import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, RadioLabel } from '@weezevent/nacre';
import AddressForm from '../events/config/addressForm';

import Styles from '../../styles/eventCreation/eventCreation.module.css';
import { useSegment } from '../../components/tracking/trackingProvider';

export default function EventCreationLocalization({
    onChange,
    onChangeObject,
    onDone = null,
    formEvent,
    platformOptions = [],
    autoMode,
    disabledAutoMode = false,
    errors = {},
    setAutoMode = () => {},
    isSubmitting,
    children,
    onCardDone = () => {},
    edit = false
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const resetForm = {
        address: {
            name: '',
            address1: '',
            address2: '',
            city: '',
            country: '',
            state: '',
            zip_code: ''
        },
        full_address: null,
        communication_platform: null,
        website: ''
    };

    const fieldErrors = React.useMemo(() => {
        if (typeof errors === 'object' && 'website' in errors) {
            return <Form.FieldErrors errors={[t(errors['website'])]} />;
        }
        return null;
    }, [errors, t]);

    const _onDone = React.useCallback(() => {
        onDone(onCardDone);
    }, [onCardDone, onDone]);

    return (
        <>
            <Form.Field style={{ marginBottom: '0' }}>
                <Form.Label label={t('ticket.creationEvent.localization.formLabelType')} />
                <div>
                    <RadioLabel
                        className={Styles['radio-label-custom']}
                        inline
                        bordered
                        name="location"
                        checked={formEvent?.localization_type === 'location'}
                        label={t('ticket.creationEvent.localization.formLabelTypeLocation')}
                        onClick={() => {
                            onChangeObject({
                                ...{ localization_type: 'location' },
                                ...resetForm
                            });
                            track('ACTION > SELECT PLACE TYPE', {
                                'Place type': 'Location',
                                'Page Title': 'CREATE EVENT'
                            });
                        }}
                    />
                    <RadioLabel
                        className={Styles['radio-label-custom']}
                        inline
                        bordered
                        name="online"
                        checked={formEvent?.localization_type === 'online'}
                        label={t('ticket.creationEvent.localization.formLabelTypeOnline')}
                        onClick={() => {
                            onChangeObject({
                                ...{ localization_type: 'online' },
                                ...resetForm
                            });
                            track('ACTION > SELECT PLACE TYPE', {
                                'Place type': 'Online',
                                'Page Title': 'CREATE EVENT'
                            });
                        }}
                    />
                    {['online_donations', 'memberships'].includes(formEvent.subtype) && (
                        <RadioLabel
                            className={Styles['radio-label-custom']}
                            inline
                            bordered
                            name="no_location"
                            checked={formEvent?.localization_type === 'no_location'}
                            label={t('ticket.creationEvent.localization.formLabelTypeNoLocation')}
                            onClick={() => {
                                onChangeObject({
                                    ...{ localization_type: 'no_location' },
                                    ...resetForm
                                });
                                track('ACTION > SELECT PLACE TYPE', {
                                    'Place type': 'None',
                                    'Page Title': 'CREATE EVENT'
                                });
                            }}
                        />
                    )}
                </div>
            </Form.Field>
            {formEvent?.localization_type === 'location' && (
                <AddressForm formEvent={formEvent} onChange={onChangeObject} autoMode={autoMode} onAutoModeChange={setAutoMode} disabledAutoMode={disabledAutoMode} />
            )}
            {formEvent?.localization_type === 'online' && (
                <>
                    {!edit && (
                        <Form.Field>
                            <Form.Label label={t('ticket.creationEvent.localization.formLabelPlatform')} optionalText={t('ticket.common.labelOptional')} />
                            <Form.Select
                                value={formEvent?.communication_platform}
                                onChange={value => {
                                    onChange('communication_platform', value);
                                }}
                                options={platformOptions}
                                placeholder={t('ticket.common.action.select')}
                            />
                        </Form.Field>
                    )}
                    <Form.Field>
                        <Form.Label label={t('ticket.creationEvent.localization.formLabelWebsite')} />
                        <Form.Input
                            name="website"
                            placeholder={t('ticket.creationEvent.localization.formPlaceholderWebsite')}
                            value={formEvent?.website}
                            onChange={({ target: { value } }) => {
                                if (value.length >= 80) {
                                    // max length
                                    return;
                                }
                                onChange('website', value);
                            }}
                        />
                    </Form.Field>
                    {fieldErrors && (edit ? fieldErrors : <Form.Field>{fieldErrors}</Form.Field>)}
                </>
            )}
            <div className={Styles['buttons-submit']}>
                {onDone && (
                    <Button primary className={Styles['button-continue']} label={t('ticket.common.action.continue')} isLoading={isSubmitting} onClick={_onDone} disabled={false} />
                )}
                {children}
            </div>
        </>
    );
}

EventCreationLocalization.propTypes = {
    onChange: PropTypes.func.isRequired,
    onChangeObject: PropTypes.func.isRequired,
    onDone: PropTypes.func,
    onCardDone: PropTypes.func,
    formEvent: PropTypes.object.isRequired,
    platformOptions: PropTypes.array,
    autoMode: PropTypes.bool.isRequired,
    setAutoMode: PropTypes.func,
    disabledAutoMode: PropTypes.bool,
    isSubmitting: PropTypes.bool.isRequired,
    children: PropTypes.node,
    edit: PropTypes.bool
};
